import React, { useEffect, useRef, useState } from "react";
import { searchPlaces, viewboxAround } from "../lib/geo";
import { MagnifyingGlass, MapPin, X } from "@phosphor-icons/react";

const RECENT_KEY = "nexora-recent-places";
const SMART_QUERIES = [
  { label: "Aeroport", query: "aeroport" },
  { label: "Ecole", query: "ecole" },
  { label: "Hopital", query: "hopital" },
  { label: "Hotel", query: "hotel" },
  { label: "Marche", query: "marche" },
  { label: "Station service", query: "station service" },
];

export default function AddressSearch({ cityCenter, onSelect, placeholder = "Chercher une adresse...", autoFocus = false, valueLabel = "" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [recentPlaces, setRecentPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const rootRef = useRef(null);
  const lastValueLabelRef = useRef("");

  useEffect(() => {
    try {
      setRecentPlaces(JSON.parse(localStorage.getItem(RECENT_KEY)) || []);
    } catch {
      setRecentPlaces([]);
    }
  }, []);

  useEffect(() => {
    if (valueLabel && valueLabel !== lastValueLabelRef.current) {
      lastValueLabelRef.current = valueLabel;
      setQ(valueLabel);
    }
    if (!valueLabel && lastValueLabelRef.current) {
      lastValueLabelRef.current = "";
      if (!open) setQ("");
    }
  }, [open, valueLabel]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (q.trim().length < 3) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      const vb = cityCenter ? viewboxAround(cityCenter, 50) : null;
      const r = await searchPlaces(q, vb, 6);
      setResults(r);
      setLoading(false);
      setOpen(true);
    }, 450);
    return () => clearTimeout(timerRef.current);
  }, [q, cityCenter]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const saveRecentPlace = (place) => {
    const item = {
      lat: Number(place.lat),
      lng: Number(place.lng),
      label: place.label || place.address || place.short || "Lieu recent",
      short: place.short || place.label || "Lieu recent",
      address: place.address,
      type: place.type,
    };
    const next = [item, ...recentPlaces.filter((p) => `${p.lat},${p.lng}` !== `${item.lat},${item.lng}`)].slice(0, 6);
    setRecentPlaces(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  };

  const choosePlace = (place) => {
    const selected = { ...place, short: place.short || place.label || "Lieu recent" };
    setQ(selected.short);
    setOpen(false);
    saveRecentPlace(selected);
    onSelect(selected);
  };

  const chooseSmartQuery = async (item) => {
    setQ(item.label);
    setLoading(true);
    const vb = cityCenter ? viewboxAround(cityCenter, 50) : null;
    const found = await searchPlaces(item.query, vb, 5);
    setLoading(false);
    if (found.length > 0) {
      choosePlace(found[0]);
      return;
    }
    setResults([]);
    setOpen(true);
  };

  return (
    <div ref={rootRef} className="relative w-full" data-testid="address-search">
      <div className="relative">
        <MagnifyingGlass size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" weight="bold" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="w-full rounded-xl border border-white/10 bg-white/5 py-3 pl-9 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="address-input"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setResults([]); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-1 text-muted-foreground hover:text-foreground"
            data-testid="address-clear"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full z-[1400] mt-2 max-h-[42dvh] overflow-y-auto rounded-2xl border border-white/10 bg-[#1a1b20]/95 p-1 shadow-2xl shadow-black/40 backdrop-blur-xl">
          {q.trim().length < 3 && recentPlaces.length > 0 && (
            <>
              <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Lieux recents</div>
              {recentPlaces.map((r, i) => (
                <button
                  key={`${r.lat},${r.lng},recent-${i}`}
                  type="button"
                  onClick={() => choosePlace(r)}
                  className="flex w-full items-start gap-2 rounded-xl px-2 py-2 text-left hover:bg-white/10"
                  data-testid={`recent-place-${i}`}
                >
                  <MapPin size={14} weight="fill" className="mt-0.5 shrink-0 text-secondary" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold leading-tight">{r.short}</div>
                    <div className="line-clamp-2 text-[10px] leading-tight text-muted-foreground">{r.label}</div>
                  </div>
                </button>
              ))}
            </>
          )}

          {q.trim().length < 3 && recentPlaces.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Vos lieux recents apparaitront ici</div>
          )}

          {q.trim().length < 3 && (
            <>
              <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Suggestions intelligentes</div>
              <div className="grid grid-cols-2 gap-1 p-1">
                {SMART_QUERIES.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => chooseSmartQuery(item)}
                    className="rounded-xl bg-white/5 px-3 py-2 text-left text-xs font-bold hover:bg-white/10"
                    data-testid={`smart-place-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {q.trim().length >= 3 && loading && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Recherche...</div>
          )}

          {q.trim().length >= 3 && !loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Aucun resultat</div>
          )}

          {q.trim().length >= 3 && results.map((r, i) => (
            <button
              key={`${r.lat},${r.lng},${i}`}
              type="button"
              onClick={() => choosePlace(r)}
              className="flex w-full items-start gap-2 rounded-xl px-2 py-2 text-left hover:bg-white/10"
              data-testid={`address-result-${i}`}
            >
              <MapPin size={14} weight="fill" className="mt-0.5 shrink-0 text-primary" />
              <div className="min-w-0">
                <div className="truncate text-sm font-bold leading-tight">{r.short}</div>
                <div className="line-clamp-2 text-[10px] leading-tight text-muted-foreground">{r.label}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
